import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, AlertCircle, Phone, KeyRound } from "lucide-react";
import { DeliveryPhotoWall } from "../components/auth/DeliveryPhotoWall";
import { formatPhoneInput } from "../lib/utils";
import BACKEND_URL from "../lib/apiConfig";
import { applyTheme, getTheme } from "../lib/theme";
// iter 370cn — static import so we can fire the bootstrap with zero
// dynamic-import latency the moment auth/login resolves. The dedupe
// in `preloadDashboard` ensures App.js handleLogin's later call joins
// this in-flight promise instead of double-firing.
import { preloadDashboard } from "../lib/dashboardBootstrap";

const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_golfcart-manager-1/artifacts/enoqxqkh_New%20Logo%20White.png";
const BACKGROUND_URL = "https://customer-assets.emergentagent.com/job_7f530663-f5c8-4742-9222-9bc7e4f0dac5/artifacts/mm7c2lcd_exported_CF0632CF-0ADF-463F-BA19-7B6289398D83.jpeg";
// Flip to `false` to restore the single cart-photo hero (object-position 30%).
const USE_DELIVERY_SLIDESHOW = true;
// iter 370ei — bumped safety timeout 12s → 20s. The previous 12s
// budget fired during a typical "first attempt fails @ 8s + 1s backoff
// + 2nd attempt in flight" sequence on iPhone Safari with a slow
// cellular link, surfacing as Jake's "Connection timed out" toast even
// though retries would have eventually succeeded. 20s comfortably
// covers 2 attempts (8s + 1s + 8s = 17s) and bails before the 3rd
// would even start.
const LOGIN_TIMEOUT_MS = 20000;

export default function PinLogin({ onLogin }) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const pinInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const loginTimeoutRef = useRef(null);
  // iter 370ei — Cancel the auto-login probe the moment the user starts
  // typing in the manual form so we don't queue their login behind a
  // stuck health check (the original cause of the "Connecting..."
  // spinner Jake saw on iPhone Safari).
  const autoLoginAbortRef = useRef(null);

  // iter 503h — PinLogin is designed dark (bg-black + bg-neutral-950
  // + white text). The global `html.light` CSS rules force every
  // bg-black/bg-white/5/bg-neutral-950 to white !important, which
  // leaves the white labels and inputs invisible on a now-white
  // surface. Strip `.light` while the login screen is mounted;
  // restore the app default on unmount so the next page lands
  // on its own theme setup correctly.
  useEffect(() => {
    document.documentElement.classList.remove("light");
    return () => applyTheme(getTheme());
  }, []);

  // Auto-login: check for trusted device token on mount.
  // iter 370ei — runs in the BACKGROUND. Manual login form renders
  // immediately so users on slow iPhone Safari connections don't see
  // a 12-17s "Connecting…" spinner before they can type their PIN.
  useEffect(() => {
    const savedToken = localStorage.getItem("trusted_device_token");
    const savedPhone = localStorage.getItem("trusted_device_phone");
    if (!savedToken || !savedPhone) return; // nothing to do

    const ctrl = new AbortController();
    autoLoginAbortRef.current = ctrl;

    const tryAutoLogin = async () => {
      try {
        // iter 370ei — single 4s health check (was 3 × 3s + 2 × 1s = 11s).
        // If the backend isn't reachable in 4s, fall through silently —
        // the user can sign in manually.
        await axios.get(`${API}/health`, {
          timeout: 4000,
          signal: ctrl.signal,
        });
        const res = await axios.post(`${API}/auth/auto-login`, null, {
          headers: { Authorization: `Bearer ${savedToken}` },
          timeout: 5000,
          signal: ctrl.signal,
        });
        if (res.data.success) {
          localStorage.setItem("session_token", res.data.token);
          localStorage.setItem("user_id", res.data.user_id);
          localStorage.setItem("user_name", res.data.user_name);
          localStorage.setItem("user_role", res.data.role);
          localStorage.setItem("session_expires", res.data.expires_at);
          // iter 370cn — same bootstrap head-start trick on the
          // trusted-device auto-login path.
          preloadDashboard(
            (url, opts) => axios.get(url, {
              ...opts,
              _skipBootstrapCache: true,
              headers: { ...(opts?.headers || {}), Authorization: `Bearer ${res.data.token}` },
            }),
          ).catch(() => { /* non-fatal */ });
          onLogin(res.data);
          return;
        }
      } catch (err) {
        // Silent fail — user just types their PIN. Clear stale token if
        // server rejected it (so they don't see the same delay tomorrow).
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("trusted_device_token");
          localStorage.removeItem("trusted_device_phone");
        }
      }
    };
    tryAutoLogin();
    return () => ctrl.abort();
  }, [onLogin]);

  // Prefill phone from "remember me" — runs once on mount now that
  // the auto-login gate is gone (iter 370ei).
  useEffect(() => {
    const savedPhone = localStorage.getItem("remembered_phone");
    if (savedPhone) {
      setPhoneNumber(formatPhoneInput(savedPhone));
      setTimeout(() => pinInputRef.current?.focus(), 100);
    } else {
      phoneInputRef.current?.focus();
    }
  }, []);

  // Lockout countdown
  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      if (new Date() >= new Date(lockedUntil)) {
        setLockedUntil(null);
        setError("");
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  // Cleanup login timeout on unmount
  useEffect(() => {
    return () => { if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current); };
  }, []);

  // iter 348: stash the current deep-link URL so that when a tech scans a
  // shelf QR (→ `/inventory/part/:id`) or taps a customer-printout link
  // while signed out, we return them to THAT URL after auth rather than
  // the role-default dashboard. Consumed once by `PostLoginRedirect` in
  // App.js. Public routes (driver/payment/d-token) are already handled
  // as exceptions in App's unauthenticated router so they never reach
  // PinLogin — we intentionally skip them here for safety.
  useEffect(() => {
    try {
      const path = window.location.pathname + window.location.search;
      const EXCLUDED_PREFIXES = [
        "/driver/",
        "/payment-success",
        "/d/",
        "/login",
      ];
      const isExcluded =
        path === "/" ||
        path === "/mobile" ||
        EXCLUDED_PREFIXES.some((p) => path === p || path.startsWith(p));
      if (!isExcluded) {
        sessionStorage.setItem("pending_redirect", path);
      }
    } catch { /* sessionStorage may be disabled (private mode); non-fatal */ }
  }, []);

  const handlePhoneChange = (e) => {
    setPhoneNumber(formatPhoneInput(e.target.value));
    setError("");
  };

  const handlePinChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    setPin(digits);
    setError("");
    // Auto-submit on 4 digits
    if (digits.length === 4) {
      handleLogin(digits);
    }
  };

  const handleLogin = async (pinValue) => {
    if (lockedUntil) return;
    const phoneDigits = phoneNumber.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      setError("Enter a valid 10-digit phone number");
      return;
    }

    // iter 370ei — Cancel any in-flight auto-login probe BEFORE manual
    // login fires. Without this, on a slow iPhone Safari connection the
    // auto-login health check (4s) and the manual `/auth/login` would
    // race for the same connection pool, sometimes leaving the manual
    // request queued behind the doomed probe.
    if (autoLoginAbortRef.current) {
      try { autoLoginAbortRef.current.abort(); } catch (_) {}
      autoLoginAbortRef.current = null;
    }

    setLoading(true);
    setError("");

    // Safety timeout — force-stop loading if stuck
    if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);
    loginTimeoutRef.current = setTimeout(() => {
      setLoading(false);
      setError("Login timed out. Tap 'Troubleshoot' below for details.");
      setPin("");
      console.error("[Login] Safety timeout reached after " + LOGIN_TIMEOUT_MS + "ms");
    }, LOGIN_TIMEOUT_MS);

    try {
      console.log("[Login] Starting login for phone ending:", phoneDigits.slice(-4));
      // Retry login up to 2 times on network/server errors
      let res;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          console.log(`[Login] Attempt ${attempt + 1}/3...`);
          res = await axios.post(
            `${API}/auth/login`,
            { phone: phoneDigits, pin: pinValue, remember_me: true },
            {
              timeout: 8000,
              headers: { "X-Client-Info": navigator.userAgent.slice(0, 200) },
            }
          );
          console.log("[Login] API responded:", res.status);
          break;
        } catch (retryErr) {
          console.warn(`[Login] Attempt ${attempt + 1} failed:`, retryErr.message, retryErr.response?.status);
          if (attempt < 2 && (!retryErr.response || retryErr.response.status >= 500)) {
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            continue;
          }
          throw retryErr;
        }
      }
      if (res.data.success) {
        console.log("[Login] Success — storing session");
        // Always remember the phone number
        localStorage.setItem("remembered_phone", phoneDigits);
        localStorage.setItem("session_token", res.data.token);
        localStorage.setItem("user_id", res.data.user_id);
        localStorage.setItem("user_name", res.data.user_name);
        localStorage.setItem("user_role", res.data.role);
        localStorage.setItem("session_expires", res.data.expires_at);

        // Trust device = auto-login next time (no PIN needed)
        if (trustDevice && res.data.token) {
          localStorage.setItem("trusted_device_token", res.data.token);
          localStorage.setItem("trusted_device_phone", phoneDigits);
        }

        // iter 370cn — kick off dashboard bootstrap RIGHT NOW so the
        // ~1.2s round-trip races in parallel with React's state +
        // mount cascade. Fire-and-forget; the dedupe in
        // dashboardBootstrap.js makes App.js handleLogin's later call
        // a no-op join. Net effect: ~50-100ms head start on the
        // dashboard mount → most of the bootstrap GETs hit cache by
        // the time the dashboard's data hooks fire.
        preloadDashboard(
          (url, opts) => axios.get(url, {
            ...opts,
            _skipBootstrapCache: true,
            headers: { ...(opts?.headers || {}), Authorization: `Bearer ${res.data.token}` },
          }),
        ).catch(() => { /* non-fatal */ });

        onLogin(res.data);
      }
    } catch (err) {
      console.error("[Login] Error:", err.message, err.response?.status, err.code);
      if (err.response?.status === 423) {
        setError(err.response.data.detail);
        const match = err.response.data.detail.match(/(\d+) minutes/);
        if (match) {
          setLockedUntil(new Date(Date.now() + parseInt(match[1]) * 60 * 1000).toISOString());
        }
      } else if (err.response?.status === 401) {
        setError("Invalid phone number or PIN");
        // iter 370bd — /login itself now increments the failed-attempt
        // counter server-side, so we no longer need the client to call
        // /login/failed. (Endpoint kept as a 200 no-op for older
        // builds.)
      } else if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        setError("Connection timed out. Check your internet and try again, or tap 'Troubleshoot' below.");
      } else if (!err.response) {
        setError("Cannot reach server. Check your connection, or tap 'Troubleshoot' below.");
      } else {
        setError("Login failed. Please try again.");
      }
      setPin("");
      pinInputRef.current?.focus();
    } finally {
      if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);
      setLoading(false);
    }
  };

  const getRemainingLockTime = () => {
    if (!lockedUntil) return null;
    const remaining = Math.max(0, Math.ceil((new Date(lockedUntil) - new Date()) / 1000));
    return `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, "0")}`;
  };

  // iter 370ei — DROPPED the `!autoLoginChecked` blocking spinner.
  // Auto-login now runs in the background (see effect above); the
  // manual form renders immediately so iPhone Safari users never see
  // a 12-17s "Connecting…" hang on stale-token devices.

  const phoneDigits = phoneNumber.replace(/\D/g, "");
  const phoneReady = phoneDigits.length === 10;

  return (
    <div className="h-screen bg-black flex overflow-hidden" data-testid="login-screen">
      {/* LEFT 60% — decorative photo wall (hidden below lg) */}
      <div className="hidden lg:block lg:w-3/5 relative">
        {USE_DELIVERY_SLIDESHOW ? (
          <DeliveryPhotoWall apiBase={API} />
        ) : (
          <img
            src={BACKGROUND_URL}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "30% center" }}
          />
        )}
      </div>

      {/* RIGHT 40% — sign-in card */}
      <div className="flex-1 lg:w-2/5 flex items-center justify-center bg-neutral-950">
        <div className="w-full max-w-sm px-6 py-12 sm:px-10 flex flex-col items-center">
          {/* Logo */}
          <img src={LOGO_URL} alt="Jake's Custom Carts" className="h-14 object-contain mb-10" />

          <div className="w-full">
            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="login-error">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Lockout */}
            {lockedUntil && (
              <div className="text-center py-3 mb-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-yellow-400 text-sm font-medium">Try again in {getRemainingLockTime()}</p>
              </div>
            )}

            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (pin.length === 4) handleLogin(pin);
              }}
            >
              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-white/60 text-xs font-bold tracking-wider uppercase mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50 pointer-events-none" />
                  <input
                    id="phone"
                    ref={phoneInputRef}
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    disabled={loading || !!lockedUntil}
                    data-testid="phone-input"
                    className="w-full h-12 pl-10 pr-3 bg-white/5 border border-white/10 text-white placeholder:text-white/40 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    onKeyDown={(e) => { if (e.key === "Enter" && phoneReady) pinInputRef.current?.focus(); }}
                  />
                </div>
              </div>

              {/* PIN */}
              <div>
                <label htmlFor="pin" className="block text-white/60 text-xs font-bold tracking-wider uppercase mb-2">
                  4-Digit PIN
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50 pointer-events-none" />
                  <input
                    id="pin"
                    ref={pinInputRef}
                    type="password"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={4}
                    value={pin}
                    onChange={handlePinChange}
                    disabled={loading || !!lockedUntil}
                    data-testid="pin-input"
                    className="w-full h-12 pl-10 pr-3 bg-white/5 border border-white/10 text-white placeholder:text-white/40 rounded-lg text-center text-lg tracking-[0.5em] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                </div>
              </div>

              {/* Remember device */}
              <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer select-none" data-testid="trust-device-label">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  data-testid="trust-device-checkbox"
                  className="w-4 h-4 rounded bg-white/5 border-white/20 accent-blue-600"
                />
                Remember this device
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !!lockedUntil || !phoneReady || pin.length !== 4}
                data-testid="login-btn"
                className="w-full h-12 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold uppercase tracking-wider text-sm transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="mt-10 text-white/30 text-[11px] tracking-wide">
            Jake&apos;s Custom Carts · Dispatch Portal
          </p>
        </div>
      </div>
    </div>
  );
}
